import React from 'react';
import { Sparkles, Shield, Zap, Users } from 'lucide-react';

const categories = [
  {
    title: "Cosmic Teams",
    examples: ["Stellar Guardians", "Nova Force", "Quantum Knights"],
    description: "Teams that protect the cosmos and deal with galactic threats."
  },
  {
    title: "Urban Protectors",
    examples: ["Night Watchers", "Metro Defenders", "Shadow Corps"],
    description: "Street-level heroes who protect their cities from crime and corruption."
  },
  {
    title: "Mystic Orders",
    examples: ["Arcane Circle", "Phoenix Order", "Mystic Council"],
    description: "Teams that defend against supernatural and magical threats."
  }
];

const tips = [
  {
    icon: Sparkles,
    title: "Consider Your Theme",
    content: "Choose a name that reflects your team's primary focus: cosmic, urban, mystical, or tech-based."
  },
  {
    icon: Shield,
    title: "Power Dynamics",
    content: "Incorporate elements that represent your team's collective powers and abilities."
  },
  {
    icon: Zap,
    title: "Memorable Impact",
    content: "Create names that are easy to remember and command respect."
  },
  {
    icon: Users,
    title: "Team Unity",
    content: "Ensure the name represents the collective identity of all team members."
  }
];

export default function SuperheroGuide() {
  return (
    <section className="py-20 bg-violet-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
          Complete Guide to Superhero Team Names
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {categories.map((category, index) => (
            <div key={index} className="bg-white p-6 rounded-lg shadow-md transform transition-all duration-300 hover:scale-105">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">{category.title}</h3>
              <ul className="space-y-2 mb-4">
                {category.examples.map((example, i) => (
                  <li key={i} className="text-violet-600">{example}</li>
                ))}
              </ul>
              <p className="text-gray-600">{category.description}</p>
            </div>
          ))}
        </div>

        <div className="bg-white p-8 rounded-lg shadow-md">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Tips for Choosing Your Superhero Team Name
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {tips.map((tip, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-violet-100 mb-4">
                  <tip.icon className="w-6 h-6 text-violet-600" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">{tip.title}</h4>
                <p className="text-gray-600">{tip.content}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 bg-violet-100 p-8 rounded-xl">
          <h3 className="text-2xl font-semibold text-gray-900 mb-6">
            Advanced Naming Strategies
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h4 className="font-semibold text-violet-800 mb-4">Name Elements to Consider:</h4>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-violet-500 rounded-full"></div>
                  <span>Primary power source or ability</span>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-violet-500 rounded-full"></div>
                  <span>Team's mission or purpose</span>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-violet-500 rounded-full"></div>
                  <span>Geographic or cosmic significance</span>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-violet-500 rounded-full"></div>
                  <span>Mythological or historical references</span>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-violet-800 mb-4">Common Naming Patterns:</h4>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-violet-500 rounded-full"></div>
                  <span>Adjective + Collective Noun (Mighty Defenders)</span>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-violet-500 rounded-full"></div>
                  <span>Power + Team Type (Force Squadron)</span>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-violet-500 rounded-full"></div>
                  <span>Location + Heroes (Metro Champions)</span>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-violet-500 rounded-full"></div>
                  <span>Element + Organization (Quantum Legion)</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}